import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import TourBar from '@/components/layout/tour-bar';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { ReportWindowProvider } from '@/providers/report-window';
import { CommandPalette } from './command-palette';
import { FindReport } from '@/components/reports/find-report';
import { useReportsV2 } from '@/lib/reports-v2';
import { useEcommerce } from '@/hooks/use-settings';
import { MobileBottomNav } from './mobile-bottom-nav';
import { Toaster } from '@/components/ui/toaster';
import { AIAssistantLauncher } from '@/components/ai-assistant/ai-assistant';
import { RouteErrorBoundary } from '@/components/error-boundary';
import { PlanBanner } from '@/components/layout/plan-banner';
import { WaTokenBanner } from '@/components/layout/wa-token-banner';
import { TrackingLossBanner } from '@/components/layout/tracking-loss-banner';
import { BannerStack } from '@/components/ui/calm';
import { CurrencyConfirmBanner } from '@/components/settings/currency-choice';
import { ConversionDefaultBanner } from '@/components/settings/conversion-default';
import { PageFrameSkeleton } from '@/components/ui/skeleton-kit';
import { ReportActivity, RouteFeel } from './route-feel';
import { QbeeProvider } from '@/components/qbee/qbee-context';
import { QbeeDock } from '@/components/qbee/qbee-dock';
import { HOSTING_HOME, useHostingOnly, useWorkspaceFocus } from '@/hooks/use-hosting-only';
import { openInFocus } from '@/lib/workspace-modules';

const SIDEBAR_KEY = 'mm-sidebar-collapsed';

// Superadmin impersonation banner: the impersonation JWT carries `imp: true`
// (and /me echoes it back with org_name), so a provider stepping into a tenant
// workspace always sees WHICH workspace they're inside. Invisible to tenants.
function ImpersonationBanner() {
  const { data: me } = useQuery({
    queryKey: ['auth', 'me-shell'],
    queryFn: async () => (await api.get('/api/auth/me')).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
  if (!me?.imp) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 border-b card-attention px-3 py-1.5 text-center text-xs font-medium text-amber-800 dark:text-amber-300">
      <ShieldAlert className="size-3.5 shrink-0" />
      <span>Superadmin session · you are inside <span className="font-semibold">{me.org_name || 'a workspace'}</span></span>
      <span className="text-amber-700/70 dark:text-amber-400/60">· actions run as this workspace's admin · token expires in ~2h</span>
    </div>
  );
}

// A focused workspace (a Server GTM hosting signup, lib/workspace-modules)
// opens Server GTM, its cards, settings and the sections of the cards it
// switched on; any other address, the home page included, lands on Server GTM.
function HostingOnlyGate({ children }) {
  const { focus, modules } = useWorkspaceFocus();
  const { pathname } = useLocation();
  if (focus && !openInFocus(pathname, modules)) return <Navigate to={HOSTING_HOME} replace />;
  return children;
}

// Shown only while a lazy page chunk downloads (first visit per chunk; cached
// after, and usually fetched on hover before the click). The page's own frame
// in outline - header, tiles, a panel - so the first paint is the shape of a
// report and the page lands into it instead of replacing a centred spinner.
function RouteFallback() {
  return <PageFrameSkeleton />;
}

export function AppShell({ user, onLogout }) {
  const hostingOnly = useHostingOnly();
  const { on: reportsV2 } = useReportsV2();
  const { enabled: ecomOn } = useEcommerce();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0');
    } catch {}
  }, [collapsed]);

  // Trigger to open command palette from outside (via window event for now)
  const onOpenSearch = () => {
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
  };

  // On mobile, hide the bottom nav while scrolling DOWN (reading) and reveal it
  // on scroll up - reclaims the bottom strip for content. Desktop has no bar.
  const mainRef = useRef(null);
  const [navHidden, setNavHidden] = useState(false);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    let last = 0;
    const onScroll = () => {
      const top = el.scrollTop;
      if (top < 40) setNavHidden(false);
      else if (top > last + 6) setNavHidden(true);
      else if (top < last - 6) setNavHidden(false);
      last = top;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // The app's ground is the rail's own colour, so the rail has nothing to be
  // bordered against. The content panel carries the only edge, and it is a curve.
  return (
    // The reporting window wraps the whole shell: the Topbar renders the
    // control and every routed page reads the same window from it.
    <ReportWindowProvider>
    {/* Qbee, the assistant: its dock sits beside the page (components/qbee). */}
    <QbeeProvider>
    {/* h-full, not h-screen. On displays 1920px and wider App.css zooms
        <html> by 6-30%, and inside a zoomed root 100vh resolves to MORE than
        the window - so the shell overflowed by 64px on a 2000px screen and
        the document grew a second scrollbar that hid the topbar. html, body
        and #root are height:100%, which is the real window at any zoom. */}
    <div className="flex h-full w-full flex-col overflow-hidden bg-sidebar text-foreground print:block print:h-auto print:overflow-visible print:bg-white">
      {/* One notice at a time, most important first; the rest fold into
          "N more". Four full-width strips stacked above the topbar pushed
          every page down and read as the product shouting. */}
      <div className="print:hidden"><BannerStack>
        {/* Full-width so it spans sidebar + content - unmissable during support. */}
        <ImpersonationBanner />
        {/* An admin whose workspace has never said which currency it works in:
            every amount is labelled by a guess until they answer, so it asks
            above every page until then (one Save, never on click). */}
        {/* Neither applies to a hosting workspace: it reports no amounts and
            sends no conversions (api services/hostingPlan). */}
        {!hostingOnly && <CurrencyConfirmBanner />}
        {/* A lead conversion is on and nobody chose what a converted lead with
            no deal value is worth: it asks until answered (explicit Save). */}
        {!hostingOnly && <ConversionDefaultBanner />}
        {/* The tag is off the live site: every report is missing its site
            activity until it is back, so it is said above every page. */}
        <TrackingLossBanner />
        <WaTokenBanner />
        {/* Plan expiry notice - informational only, nothing is switched off. */}
        <PlanBanner />
      </BannerStack></div>
      <div className="flex min-h-0 flex-1 overflow-hidden print:block print:overflow-visible">
      {/* Sidebar visible on tablet+ only. Mobile gets the bottom nav.
          min-h-0 so this wrapper can never be stretched past the row by what is
          inside it - without it a too-tall rail pushed the whole shell beyond
          the viewport and made the page itself scroll. */}
      <div className="hidden min-h-0 md:flex print:!hidden">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      </div>

      <div className="flex flex-1 flex-col overflow-hidden print:block print:overflow-visible">
        <Topbar user={user} onLogout={onLogout} onOpenSearch={onOpenSearch} />
        {/* Pad bottom on mobile so the fixed bottom-nav (h-14) doesn't cover
            the last row of content. md:pb-0 because desktop has no nav bar.
            overflow-x-hidden on mobile clips any stray wide content (e.g. a
            poorly-wrapping toolbar) instead of triggering a horizontal page
            scroll - which would shift everything right and crop the left edge. */}
        {/* THE CURVE.
            Two hard 1px rules used to meet at this corner - the rail's right
            border and the topbar's bottom border - and a right angle drawn in
            hairlines is the most generic shape an interface can make. The
            content is a surface that curves away from both instead, which is
            one edge rather than two and says "panel" rather than "grid".
            With Qbee open beside it (data-qbee on <html>, set by the dock) it
            curves at the top right too: two cards, side by side. */}
        <main ref={mainRef} className={cn('print:!overflow-visible print:!rounded-none print:!border-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain border-border/70 bg-background transition-[padding] duration-200 md:overflow-x-visible md:rounded-tl-2xl md:border-l md:border-t md:pb-0 lg:[html[data-qbee=open]_&]:rounded-tr-2xl lg:[html[data-qbee=open]_&]:border-r', navHidden ? 'pb-0' : 'pb-16')}>
          {/* A report fetching a new window keeps its last answer on screen,
              dimmed, under a thin line, and swaps in place (route-feel). */}
          <ReportActivity mainRef={mainRef} />
          <RouteErrorBoundary>
            {/* Suspense for lazy-loaded pages: the sidebar + topbar stay mounted,
                only this content area shows a brief loader while the chunk loads
                (and only the FIRST time - chunks are cached immutably after). */}
            <Suspense fallback={<RouteFallback />}>
              <HostingOnlyGate><Outlet /></HostingOnlyGate>
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>
      <QbeeDock />

      <RouteFeel mainRef={mainRef} />
      <div className="print:hidden"><MobileBottomNav hidden={navHidden} /></div>
      {/* Renders only while ?tour= is in the URL, which is almost never. */}
      <TourBar />
      <CommandPalette />
      {reportsV2 && <FindReport ecomOn={ecomOn} />}
      {/* Hidden until it is finished. A launcher on every screen implies a
          working feature, and one that half-answers costs more trust than its
          absence - especially on a product whose whole pitch is that the
          numbers can be relied on. Restore by deleting the false. */}
      {false && <AIAssistantLauncher />}
      <Toaster />
      </div>
    </div>
    </QbeeProvider>
    </ReportWindowProvider>
  );
}
